/**
 * SessionPlannerScreen — plan + edit + save + complete a session (PRD §3 #3).
 *
 * v1.0-B wires the full session-planner surface:
 *   - Session header (student name, scheduled_at, status).
 *   - Goals multi-select (defaults to all active goals; clinician
 *     can toggle).
 *   - "Generate Plan" CTA → supabase.functions.invoke(
 *     'api-generate-session-plan', { body: { session_id,
 *     focus_goal_ids } }).
 *   - Plan view: editable warm-up + 3 activity blocks + 3
 *     data-collection prompts + closing + parent-update prompt.
 *   - "Save Plan" → UPDATE sessions SET plan_text = ...
 *   - "Mark Complete" → UPDATE sessions SET completed_at = now()
 *     + navigates to the SessionNotes placeholder route (v1.0-C
 *     fills in the notes capture screen).
 *   - Free-tier gate: if ai_consent_granted_at is null or
 *     stale, the Generate button is disabled with a message.
 *   - Free-tier counter: badge on the Generate button showing
 *     "<used> of 10 free generations used this month".
 *
 * Conventions mirror ReceiptScreen.tsx + StudentDetailScreen.tsx:
 *   * `: ReactElement` return type.
 *   * Tailwind via the existing class names.
 *   * `void (async () => { ... })()` for fire-and-forget promises.
 *   * Cancellation guard on every async effect.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../lib/supabase/client';
import {
  formatShortDate,
  labelForGoalArea,
  labelForSessionStatus,
} from '../lib/effectiveslp/domain';
import { EdgeCallError } from '../lib/session/edgeCalls';
import { isPlanEmpty } from '../lib/session/planEmpty';
import { useAuth } from '../lib/auth/useAuth';
import { InitialEvalForm } from './InitialEvalForm';
import type { InitialEvaluation } from '../lib/effectiveslp/eval';
import { searchActivities, type LibraryActivity } from '../lib/effectiveslp/activities';
import type { GoalArea } from '../lib/effectiveslp/domain';
import { SessionPlannerLayout } from '../components/SessionPlannerLayout';
import { PlanEditor } from '../components/PlanEditor';
import { PlanPreview } from '../components/PlanPreview';
import { GenerationProgress } from '../components/GenerationProgress';
import { GeneratePlanButton } from '../components/GeneratePlanButton';

interface Props {
  sessionId: string;
  onBack: () => void;
  onOpenNotes: (sessionId: string) => void;
  /**
   * Navigate to the /upgrade paywall. Wired up by the router
   * so the SLP can escape the free-tier gate without leaving
   * the planner flow.
   */
  onNavigateUpgrade?: () => void;
}

interface SessionRow {
  id: string;
  student_id: string;
  scheduled_at: string;
  completed_at: string | null;
  status: string;
  plan_text: string | null;
  session_type: string;
  eval_data: Record<string, unknown> | null;
}

interface StudentRow {
  id: string;
  first_name: string;
  age_years: number | null;
  disorder_category: string;
  parent_email: string | null;
  parent_first_name: string | null;
}

interface GoalRow {
  id: string;
  area: string;
  goal_text: string;
  target_accuracy: number | null;
  current_accuracy: number | null;
  status: string;
}

interface UserEntitlementRow {
  ai_consent_granted_at: string | null;
  ai_generations_count: number;
  students_enabled: number;
}

interface PlanActivity {
  name: string;
  duration_min: number;
  cues: string;
  materials: string;
}

interface PlanResponse {
  warmup: string;
  target_activities: PlanActivity[];
  data_collection_prompts: string[];
  closing: string;
  parent_update_prompt: string;
}

interface PlanEnvelope {
  plan: PlanResponse;
  plan_text: string;
  log_id: string | null;
  model_used?: string;
  /** Optional structured payload from the Worker when available. */
  model_used_full?: {
    family: string;
    model: string;
    vendor: string;
  };
}

const FREE_TIER_LIMIT = 10;
const AI_CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

function isConsentFresh(iso: string | null): boolean {
  if (!iso) return false;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return false;
  return Date.now() - ms <= AI_CONSENT_MAX_AGE_MS;
}

function editablePlanFromEnvelope(env: PlanEnvelope): {
  warmup: string;
  activities: PlanActivity[];
  prompts: string[];
  closing: string;
  parentUpdate: string;
} {
  return {
    warmup: env.plan.warmup,
    activities: env.plan.target_activities.map((a) => ({ ...a })),
    prompts: [...env.plan.data_collection_prompts],
    closing: env.plan.closing,
    parentUpdate: env.plan.parent_update_prompt,
  };
}

/** Parse a saved plan_text markdown blob back into editable plan state. */
function parsePlanText(text: string): {
  warmup: string;
  activities: PlanActivity[];
  prompts: string[];
  closing: string;
  parentUpdate: string;
} {
  const result = {
    warmup: '',
    activities: [] as PlanActivity[],
    prompts: [] as string[],
    closing: '',
    parentUpdate: '',
  };
  const lines = text.split('\n');
  let section = '';
  let currentActivity: Partial<PlanActivity> | null = null;

  function flushActivity(): void {
    if (currentActivity?.name) {
      result.activities.push({
        name: currentActivity.name,
        duration_min: currentActivity.duration_min ?? 0,
        cues: currentActivity.cues ?? '',
        materials: currentActivity.materials ?? '',
      });
    }
    currentActivity = null;
  }

  for (const line of lines) {
    if (line.startsWith('## ')) {
      flushActivity();
      section = line.replace(/^## /, '');
      continue;
    }
    if (line.startsWith('### ')) {
      flushActivity();
      // "1. Name (10 min)"
      const m = line.match(/^### \d+\. (.+) \((\d+) min\)/);
      if (m?.[1] != null && m?.[2] != null) {
        currentActivity = { name: m[1], duration_min: parseInt(m[2], 10) };
      }
      continue;
    }
    if (line.startsWith('- Cues: ') && currentActivity) {
      currentActivity.cues = line.replace('- Cues: ', '');
      continue;
    }
    if (line.startsWith('- Materials: ') && currentActivity) {
      currentActivity.materials = line.replace('- Materials: ', '');
      continue;
    }
    if (section === 'Warm-up') {
      result.warmup += (result.warmup ? '\n' : '') + line;
    } else if (section === 'Data-collection prompts') {
      const m = line.match(/^\d+\. (.+)/);
      const prompt = m?.[1];
      if (prompt != null) result.prompts.push(prompt);
    } else if (section === 'Closing') {
      result.closing += (result.closing ? '\n' : '') + line;
    } else if (section === 'Parent update prompt') {
      result.parentUpdate += (result.parentUpdate ? '\n' : '') + line;
    }
  }
  flushActivity();
  return result;
}

export interface PlanSectionHeaderProps {
  studentName?: string | null;
  session: { completed_at: string | null } | null;
  editingPlan: boolean;
  previewMode: boolean;
  completing: boolean;
  onTogglePreview: () => void;
  onComplete: () => void;
}

export function PlanSectionHeader({
  studentName,
  session,
  editingPlan,
  previewMode,
  completing,
  onTogglePreview,
  onComplete,
}: PlanSectionHeaderProps): ReactElement {
  return (
    <header className="plan-section-header" data-testid="plan-section-header">
      <h2 className="onboarding-eyebrow">SESSION PLAN</h2>
      <span className="tagline">· {studentName ?? 'Student'}</span>
      <div className="plan-section-header-meta">
        {editingPlan && (
          <button
            type="button"
            className="plan-preview-toggle"
            data-active={previewMode ? 'true' : 'false'}
            onClick={onTogglePreview}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </button>
        )}
        {session && (
          <button
            type="button"
            className="plan-mark-complete-btn"
            disabled={completing || session.completed_at != null}
            onClick={() => void onComplete()}
          >
            {completing
              ? 'Completing…'
              : session.completed_at != null
                ? 'Completed'
                : 'Mark Complete'}
          </button>
        )}
      </div>
    </header>
  );
}

export function SessionPlannerScreen({
  sessionId,
  onBack,
  onOpenNotes,
  onNavigateUpgrade = () => undefined,
}: Props): ReactElement {
  const { user } = useAuth();
  const [session, setSession] = useState<SessionRow | null>(null);
  const [student, setStudent] = useState<StudentRow | null>(null);
  const [goals, setGoals] = useState<GoalRow[]>([]);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [entitlement, setEntitlement] = useState<UserEntitlementRow | null>(null);
  const [generationsThisMonth, setGenerationsThisMonth] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [completing, setCompleting] = useState(false);
  // ESLP-67: parent share
  const [copied, setCopied] = useState(false);
  // ESLP-64: eval save state
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // ESLP-66: activity library filter + search
  const [activityFilter, setActivityFilter] = useState<GoalArea | 'all'>('all');
  const [activitySearch, setActivitySearch] = useState('');
  // Plan edit mode toggle
  const [editingPlan, setEditingPlan] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  // Model name surfaced to the GenerationProgress skeleton so the
  // SLP knows which model is in flight.
  const [lastModelName, setLastModelName] = useState<string | null>(null);
  const [plan, setPlan] = useState<{
    warmup: string;
    activities: PlanActivity[];
    prompts: string[];
    closing: string;
    parentUpdate: string;
  } | null>(null);

  // ----- Initial load -----
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data: sRow, error: sErr } = await supabase
          .from('sessions')
          .select('id, student_id, scheduled_at, completed_at, status, plan_text, session_type, eval_data')
          .eq('id', sessionId)
          .maybeSingle();
        if (cancelled) return;
        if (sErr || !sRow) {
          setError(sErr?.message ?? 'Session not found.');
          return;
        }
        const sess = sRow as SessionRow;
        setSession(sess);

        // ESLP-66: restore saved plan from plan_text if it exists
        if (sess.plan_text) {
          setPlan(parsePlanText(sess.plan_text));
        }

        const [stuRes, goalsRes, entRes] = await Promise.all([
          supabase
            .from('students')
            .select('id, first_name, age_years, disorder_category, parent_email, parent_first_name')
            .eq('id', sess.student_id)
            .maybeSingle(),
          supabase
            .from('goals')
            .select('id, area, goal_text, target_accuracy, current_accuracy, status')
            .eq('student_id', sess.student_id)
            .eq('status', 'active')
            .order('created_at'),
          user
            ? supabase
                .from('users')
                .select('ai_consent_granted_at, ai_generations_count, students_enabled')
                .eq('id', user.id)
                .maybeSingle()
            : Promise.resolve({ data: null, error: null }),
        ]);
        if (cancelled) return;

        const stu = stuRes.data as StudentRow | null;
        setStudent(stu);
        const activeGoals = (goalsRes.data as GoalRow[] | null) ?? [];
        setGoals(activeGoals);
        setSelectedGoalIds(activeGoals.map((g) => g.id));
        setEntitlement((entRes.data as UserEntitlementRow | null) ?? null);

        // Count free-tier generations for this month.
        const monthStart = new Date().toISOString().slice(0, 7) + '-01T00:00:00Z';
        const { count } = await supabase
          .from('ai_generation_log')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user?.id ?? '')
          .gte('created_at', monthStart);
        if (!cancelled) setGenerationsThisMonth(count ?? 0);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load session.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, user]);

  if (!session && !error) {
    return (
      <main className="screen">
        <p className="tagline">Loading session…</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="screen">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back
        </button>
        <p className="error">{error}</p>
      </main>
    );
  }

  const isPro = (entitlement?.students_enabled ?? 2) === -1;
  const consentFresh = isConsentFresh(entitlement?.ai_consent_granted_at ?? null);
  const freeExhausted = !isPro && generationsThisMonth >= FREE_TIER_LIMIT;

  function toggleGoal(id: string): void {
    setSelectedGoalIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id],
    );
  }

  function addPrompt(): void {
    setPlan((prev) =>
      prev ? { ...prev, prompts: [...prev.prompts, ''] } : prev,
    );
  }

  function removePrompt(idx: number): void {
    setPlan((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        prompts: prev.prompts.filter((_, i) => i !== idx),
      };
    });
  }

  // ESLP-66: add a library activity to the plan
  function addActivityToPlan(act: LibraryActivity): void {
    setPlan((prev) => {
      const base = prev ?? {
        warmup: '',
        activities: [],
        prompts: [''],
        closing: '',
        parentUpdate: '',
      };
      return {
        ...base,
        activities: [
          ...base.activities,
          {
            name: act.title,
            duration_min: act.durationMin,
            cues: act.cues,
            materials: act.materials,
          },
        ],
      };
    });
  }

  function removeActivity(idx: number): void {
    setPlan((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        activities: prev.activities.filter((_, i) => i !== idx),
      };
    });
  }

  async function handleGenerate(): Promise<void> {
    if (!consentFresh || freeExhausted) return;
    setGenerating(true);
    setError(null);
    try {
      const { data, error: invErr } = await supabase.functions.invoke<PlanEnvelope>(
        'api-generate-session-plan',
        {
          body: {
            session_id: sessionId,
            focus_goal_ids: selectedGoalIds,
          },
        },
      );
      if (invErr) {
        throw new EdgeCallError(
          (invErr as unknown as { status?: number }).status ?? 500,
          { message: invErr.message, code: 'server_error' },
        );
      }
      if (!data) {
        setError('Empty response from session-plan generator.');
        return;
      }
      setPlan(editablePlanFromEnvelope(data));
      setLastModelName(
        data.model_used_full?.model ?? data.model_used ?? null,
      );
      // Reflect the new plan in local session state so the parent-share
      // section (gated on session?.plan_text) opens immediately after
      // Generate — no reload needed.
      setSession((prev) =>
        prev ? { ...prev, plan_text: data.plan_text } : prev,
      );
      setGenerationsThisMonth((c) => c + 1);
    } catch (err) {
      const message =
        err instanceof EdgeCallError
          ? `${err.code}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Generation failed.';
      setError(message);
    } finally {
      setGenerating(false);
    }
  }

  /**
   * persistPlan — persist the in-memory plan to the backend
   * using the same UPDATE sessions SET plan_text path that
   * `handleSavePlan` uses. This is what the auto-save hook in
   * PlanEditor drives (issue #437 acceptance criterion #5).
   */
  async function persistPlan(value: NonNullable<typeof plan>): Promise<void> {
    setSavingPlan(true);
    setError(null);
    try {
      const planText = serializePlan(value);
      const { error: uErr } = await supabase
        .from('sessions')
        .update({ plan_text: planText })
        .eq('id', sessionId);
      if (uErr) {
        setError(uErr.message);
        throw new Error(uErr.message);
      }
      setSession((prev) => (prev ? { ...prev, plan_text: planText } : prev));
    } finally {
      setSavingPlan(false);
    }
  }

  async function handleSavePlan(): Promise<void> {
    if (!plan) return;
    await persistPlan(plan);
  }

  async function handleComplete(): Promise<void> {
    setCompleting(true);
    setError(null);
    try {
      const nowIso = new Date().toISOString();
      const { error: uErr } = await supabase
        .from('sessions')
        .update({ status: 'completed', completed_at: nowIso })
        .eq('id', sessionId);
      if (uErr) {
        setError(uErr.message);
        return;
      }
      onOpenNotes(sessionId);
    } finally {
      setCompleting(false);
    }
  }

  // ─── ESLP-67: Parent share ───

  function parentPlanText(): string {
    if (!session?.plan_text) return '';
    // The saved plan_text is markdown — convert to parent-friendly
    // plain text by stripping the clinical sections (data-collection
    // prompts) and reformatting for readability.
    const text = session.plan_text;
    const lines = text.split('\n');
    const parentLines: string[] = [];
    let skipSection = false;
    for (const line of lines) {
      // Skip data-collection prompts section
      if (line.startsWith('## Data-collection')) {
        skipSection = true;
        continue;
      }
      if (skipSection && line.startsWith('## ')) {
        skipSection = false;
      }
      if (skipSection) continue;

      // Reformat headers for parents
      if (line.startsWith('# Session plan')) {
        parentLines.push('Speech Session Plan');
        parentLines.push('');
        if (student) {
          parentLines.push(`Student: ${student.first_name}`);
        }
        if (session.scheduled_at) {
          parentLines.push(
            `Date: ${formatShortDate(session.scheduled_at)}`,
          );
        }
        parentLines.push('');
        continue;
      }
      if (line.startsWith('## Warm-up')) {
        parentLines.push('Warm-up');
        parentLines.push('-------');
        continue;
      }
      if (line.startsWith('## Target activities')) {
        parentLines.push('Activities');
        parentLines.push('----------');
        continue;
      }
      if (line.startsWith('## Closing')) {
        parentLines.push('Closing');
        parentLines.push('-------');
        continue;
      }
      if (line.startsWith('## Parent update')) {
        parentLines.push('Notes for home');
        parentLines.push('--------------');
        continue;
      }
      if (line.startsWith('### ')) {
        parentLines.push(line.replace(/^### /, ''));
        continue;
      }
      parentLines.push(line);
    }
    return parentLines.join('\n');
  }

  async function handleCopyParentText(): Promise<void> {
    const text = parentPlanText();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to clipboard');
    }
  }

  function handleEmailParent(): void {
    const text = parentPlanText();
    const to = student?.parent_email ?? '';
    const subject = `Speech session update — ${student?.first_name ?? 'Student'}`;
    const body = encodeURIComponent(text);
    const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${body}`;
    // Use a programmatic anchor click — more reliable than
    // window.location.href for mailto: links, especially on Safari.
    const a = document.createElement('a');
    a.href = mailto;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Fallback: also copy to clipboard so the clinician can paste
    // into their email app if the mailto: handler isn't configured.
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  }

  function handlePrintPdf(): void {
    const text = parentPlanText();
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Could not open print window — check popup blocker');
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Speech Session Plan</title>
        <style>
          body { font-family: -apple-system, sans-serif; max-width: 600px; margin: 40px auto; padding: 20px; color: #333; line-height: 1.6; }
          h1 { font-size: 22px; margin-bottom: 4px; }
          h2 { font-size: 16px; margin-top: 24px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
          h3 { font-size: 14px; margin-top: 16px; }
          p { margin: 8px 0; }
          .meta { color: #666; font-size: 14px; margin-bottom: 20px; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <pre style="white-space: pre-wrap; font-family: inherit;">${text.replace(/</g, '&lt;')}</pre>
        <script>window.print();</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  }

  return (
    <main className="screen">
      <header className="screen-header">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back
        </button>
        <h1>Session plan</h1>
        <p className="tagline">
          {student?.first_name ?? 'Student'}
          {student?.age_years != null ? ` · Age ${student.age_years}` : ''}
          {' · '}
          {formatShortDate(session!.scheduled_at)}
          {' · '}
          {labelForSessionStatus(session!.status)}
        </p>
      </header>

      {/* ESLP-64: eval sessions render the structured evaluation
          template instead of the therapy planner. */}
      {(session!.session_type === 'initial_eval' || session!.session_type === 'reeval') ? (
        <InitialEvalForm
          initialData={session!.eval_data as InitialEvaluation | null}
          saving={saving}
          saved={saved}
          onSave={async (evalData) => {
            setSaving(true);
            try {
              const { error: uErr } = await supabase
                .from('sessions')
                .update({ eval_data: evalData })
                .eq('id', sessionId);
              if (uErr) {
                setError(uErr.message);
              } else {
                setSaved(true);
                setTimeout(() => setSaved(false), 3000);
              }
            } finally {
              setSaving(false);
            }
          }}
        />
      ) : (
        <SessionPlannerLayout
          className="session-planner-shell"
          leftPane={
            <>
              {/* Focus goals — stays sticky-positioned in the
                  left pane via .planner-grid-col--left. */}
              <section className="planner-pane-section planner-pane-section--goals">
                <h2 className="onboarding-eyebrow">
                  Focus goals ({selectedGoalIds.length} of {goals.length})
                </h2>
                {goals.length === 0 && (
                  <p className="tagline">No active goals for this student.</p>
                )}
                {goals.map((g) => {
                  const checked = selectedGoalIds.includes(g.id);
                  return (
                    <label key={g.id} className="caseload-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleGoal(g.id)}
                      />
                      <span className="caseload-row-body">
                        <span className="caseload-row-name">
                          {labelForGoalArea(g.area)}
                        </span>
                        <span className="caseload-row-meta">{g.goal_text}</span>
                      </span>
                    </label>
                  );
                })}
              </section>

              {/* ESLP-66: Activity library — browse pre-built activities
                  by goal area and tap to add to the plan. */}
              <section className="planner-pane-section planner-pane-section--library">
                <h2 className="onboarding-eyebrow">Activity library</h2>

                <div className="activity-search">
                  <input
                    type="text"
                    className="activity-search-input"
                    placeholder="Search activities…"
                    value={activitySearch}
                    onChange={(e) => setActivitySearch(e.target.value)}
                  />
                  {activitySearch.length > 0 && (
                    <button
                      type="button"
                      className="activity-search-clear"
                      onClick={() => setActivitySearch('')}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="chip-row" style={{ marginBottom: 12 }}>
                  <button
                    type="button"
                    className={`chip ${activityFilter === 'all' ? 'chip-active' : ''}`}
                    onClick={() => setActivityFilter('all')}
                  >
                    All
                  </button>
                  {(['articulation', 'receptive_language', 'expressive_language', 'fluency', 'social', 'aac', 'voice', 'cognitive'] as GoalArea[]).map(
                    (area) => (
                      <button
                        key={area}
                        type="button"
                        className={`chip ${activityFilter === area ? 'chip-active' : ''}`}
                        onClick={() => setActivityFilter(area)}
                      >
                        {labelForGoalArea(area)}
                      </button>
                    ),
                  )}
                </div>

                <div className="activity-library">
                  {searchActivities(activitySearch, activityFilter).map((act) => (
                    <div key={act.id} className="card activity-card">
                      <div className="activity-card-header">
                        <div>
                          <span className="caseload-row-name">{act.title}</span>
                          <div className="activity-card-meta">
                            <span className="activity-card-area">
                              {labelForGoalArea(act.goalArea)}
                            </span>
                            <span className="tagline">·</span>
                            <span className="tagline">{act.durationMin} min</span>
                            <span className="tagline">·</span>
                            <span className="tagline">Ages {act.ageRange}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="activity-add-btn"
                          onClick={() => addActivityToPlan(act)}
                        >
                          + Add
                        </button>
                      </div>
                      <p className="tagline activity-card-materials">
                        {act.materials}
                      </p>
                    </div>
                  ))}
                  {searchActivities(activitySearch, activityFilter).length === 0 && (
                    <p className="tagline activity-empty">
                      No activities match "{activitySearch}". Try a different search
                      or category.
                    </p>
                  )}
                </div>
              </section>
            </>
          }
          rightPaneSticky={
            <GeneratePlanButton
              generating={generating}
              consentFresh={consentFresh}
              freeExhausted={freeExhausted}
              isPro={isPro}
              generationsThisMonth={generationsThisMonth}
              freeTierLimit={FREE_TIER_LIMIT}
              onGenerate={() => void handleGenerate()}
              onNavigateUpgrade={onNavigateUpgrade}
            />
          }
          rightPane={
            <>
              {/* Plan section header (sticky in document flow).
                  AC #7: "SESSION PLAN" + student name + Mark
                  Complete button inline. Rendered whenever a
                  session exists so the SLP can mark a session
                  complete even before a plan has been generated
                  (regression fix: previously hidden when plan was
                  null). */}
              {session && (
                <PlanSectionHeader
                  studentName={student?.first_name ?? null}
                  session={session}
                  editingPlan={editingPlan}
                  previewMode={previewMode}
                  completing={completing}
                  onTogglePreview={() => setPreviewMode((m) => !m)}
                  onComplete={() => void handleComplete()}
                />
              )}

              {/* Empty state — no plan yet. CTA stays sticky at
                  the top of the right pane via the
                  rightPaneSticky slot above. The centered CTA
                  reuses GeneratePlanButton so the free-tier
                  exhausted lock state routes to /upgrade just
                  like the sticky header button. */}
              {!plan && !generating && (
                <div className="plan-empty" data-testid="plan-empty">
                  <div aria-hidden="true">📝</div>
                  <h3 className="plan-empty-title">Generate a plan to get started</h3>
                  <p className="plan-empty-body">
                    Pick focus goals above, then tap Generate. We'll draft a
                    warm-up, three activities, prompts to track, and a closing.
                  </p>
                  <GeneratePlanButton
                    generating={generating}
                    consentFresh={consentFresh}
                    freeExhausted={freeExhausted}
                    isPro={isPro}
                    generationsThisMonth={generationsThisMonth}
                    freeTierLimit={FREE_TIER_LIMIT}
                    onGenerate={() => void handleGenerate()}
                    onNavigateUpgrade={onNavigateUpgrade}
                  />
                </div>
              )}

              {/* Generation in flight — skeleton + estimated
                  seconds hint. */}
              {generating && (
                <GenerationProgress
                  {...(lastModelName ? { modelName: lastModelName } : {})}
                  estimatedSeconds={15}
                />
              )}

              {/* Plan preview / edit toggle (AC #6 + #8). The
                  editor stays mounted (but hidden) in Preview mode
                  so toggling back preserves the user's cursor
                  position. `hidden` is threaded into PlanEditor so
                  the focus-restore effect fires on the
                  Preview->Edit transition even when `readOnly`
                  doesn't change (i.e. when editingPlan is true
                  throughout). */}
              {plan && !generating && (
                <>
                  <div style={{ display: previewMode ? 'none' : 'block' }}>
                    <PlanEditor
                      value={plan}
                      readOnly={!editingPlan}
                      hidden={previewMode}
                      onChange={(next) => {
                        setPlan(next);
                      }}
                      onAutoSave={async (next) => {
                        if (!session) return;
                        await persistPlan(next);
                      }}
                      onRemoveActivity={(idx) => removeActivity(idx)}
                      onRemovePrompt={(idx) => removePrompt(idx)}
                      onAddPrompt={() => addPrompt()}
                    />
                  </div>
                  {previewMode && (
                    <PlanPreview
                      value={plan}
                      studentName={student?.first_name ?? null}
                    />
                  )}
                </>
              )}

              {/* Edit / Save toggle (AC #8). When the plan
                  exists but isn't being edited, show the "Edit"
                  CTA so the SLP can opt into edit mode. */}
              {plan && !generating && !previewMode && (
                <div className="plan-edit-toggle-row">
                  {!editingPlan ? (
                    <button
                      type="button"
                      className="plan-edit-btn"
                      onClick={() => setEditingPlan(true)}
                    >
                      Edit
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="snap-button"
                      disabled={savingPlan || isPlanEmpty(plan)}
                      title={
                        isPlanEmpty(plan)
                          ? 'Plan is empty'
                          : undefined
                      }
                      onClick={async () => {
                        try {
                          await handleSavePlan();
                          setEditingPlan(false);
                        } catch {
                          // persistPlan already surfaced uErr.message via
                          // setError; swallow the rejection here so the
                          // click handler doesn't produce an unhandled
                          // promise rejection. Leaving edit mode stays
                          // explicit (only on a successful save) rather
                          // than a side-effect of the await throwing.
                        }
                      }}
                    >
                      {savingPlan ? 'Saving…' : 'Save'}
                    </button>
                  )}
                </div>
              )}

              {/* ESLP-67: Parent share — copy, email, or
                  print-to-PDF. */}
              {session?.plan_text && !generating && (
                <section className="parent-share">
                  <h2 className="onboarding-eyebrow">Share with parent</h2>
                  <div className="parent-share-actions">
                    <button
                      type="button"
                      className="parent-share-btn"
                      onClick={() => void handleCopyParentText()}
                    >
                      {copied ? 'Copied!' : 'Copy text'}
                    </button>
                    <button
                      type="button"
                      className="parent-share-btn"
                      onClick={() => handleEmailParent()}
                    >
                      {copied ? 'Copied to clipboard!' : 'Email parent'}
                    </button>
                    <button
                      type="button"
                      className="parent-share-btn"
                      onClick={() => handlePrintPdf()}
                    >
                      Print / PDF
                    </button>
                  </div>
                </section>
              )}

              {error && <p className="error">{error}</p>}
            </>
          }
        />
      )}
    </main>
  );
}

function serializePlan(plan: {
  warmup: string;
  activities: PlanActivity[];
  prompts: string[];
  closing: string;
  parentUpdate: string;
}): string {
  const lines: string[] = [];
  lines.push('# Session plan');
  lines.push('');
  lines.push('## Warm-up');
  lines.push(plan.warmup);
  lines.push('');
  lines.push('## Target activities');
  plan.activities.forEach((a, idx) => {
    lines.push(`### ${idx + 1}. ${a.name} (${a.duration_min} min)`);
    lines.push('- Cues: ' + a.cues);
    lines.push('- Materials: ' + a.materials);
    lines.push('');
  });
  lines.push('## Data-collection prompts');
  plan.prompts.forEach((p, idx) => {
    lines.push(`${idx + 1}. ${p}`);
  });
  lines.push('');
  lines.push('## Closing');
  lines.push(plan.closing);
  lines.push('');
  lines.push('## Parent update prompt');
  lines.push(plan.parentUpdate);
  lines.push('');
  return lines.join('\n');
}
